import React from 'react';
import PropTypes from 'prop-types';
import {
  FaTrashAlt,
  FaShareAlt,
  FaUndo,
  FaListUl,
  FaInfoCircle,
  FaRegClock,
  FaClipboardList,
  FaIdBadge,
  FaRegCopy,
  FaPlus,
  FaExternalLinkAlt,
  FaThumbtack,
  FaMagic,
  FaSave,
  FaCloudDownloadAlt,
  FaTree,
  FaTag,
  FaStream,
  FaUserAlt,
  FaRegWindowRestore,
  FaFileAlt,
  FaEye,
  FaUserFriends,
  FaRegStar,
  FaStar,
  FaBookmark,
  FaRegBookmark,
  FaEnvelope,
  FaPhone,
  FaExchangeAlt,
  FaRegUser,
  FaRegFolderOpen,
  FaRegCommentAlt,
  FaPaperPlane,
  FaHistory,
  FaIndustry,
  FaBriefcase,
  FaUser,
  FaFilePdf,
  FaPaperclip,
  FaLink,
  FaUserPlus,
  FaCheckCircle,
  FaBan,
} from 'react-icons/fa';

import { TiArrowLeftThick, TiArrowRightThick, TiMessages, TiMessage } from 'react-icons/ti';
import { injectIntl, defineMessages } from 'react-intl';
import classNames from 'classnames';

const messages = defineMessages({
  Ok: 'Ok',
  Cancel: 'Cancel',
  Delete: 'Delete',
  Restore: 'Restore',
  Copy: 'Copy',
  Add: 'Add',
  Save: 'Save',
  Edit: 'Edit',
  Download: 'Download',
  Replace: 'Replace',
  Browse: 'Browse',
  History: 'History',
  Cancel: 'Cancel',
});

const IconPencil = injectIntl(({ className, tooltip, intl, ...rest }) => (
  <span
    className={`glyphicon glyphicon-pencil react-icon ${className}`}
    title={tooltip || intl.formatMessage(messages.Edit)}
    {...rest}
  />
));

const IconWarningSign = ({ className, ...rest }) => (
  <span className={`glyphicon glyphicon-warning-sign ${className}`} {...rest} />
);
IconWarningSign.propTypes = {
  className: PropTypes.string,
};
IconWarningSign.defaultProps = {
  className: '',
};

const DeleteIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip || intl.formatMessage(messages.Delete)} {...rest}>
    <FaTrashAlt className="react-icon" />
  </span>
));
const HistoryIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip || intl.formatMessage(messages.History)} {...rest}>
    <FaHistory className="react-icon" />
  </span>
));
const PaperPlaneIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaPaperPlane className="react-icon" />
  </span>
));

const FolderOpenIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip || intl.formatMessage(messages.Browse)} {...rest}>
    <FaRegFolderOpen className="react-icon" style={{ top: '0.17em' }} />
  </span>
));
const LeftArrowIcon = ({ ...rest }) => (
  <span {...rest}>
    <TiArrowLeftThick className="react-icon" />
  </span>
);
const RightArrowIcon = ({ ...rest }) => (
  <span {...rest}>
    <TiArrowRightThick className="react-icon" />
  </span>
);

const UndoIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip || intl.formatMessage(messages.Restore)} {...rest}>
    <FaUndo className="react-icon" />
  </span>
));
const SaveIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip || intl.formatMessage(messages.Save)} {...rest}>
    <FaSave className="react-icon" />
  </span>
));

const ListIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaListUl className="react-icon" />
  </span>
));

const InfoCircleIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaInfoCircle className="react-icon" />
  </span>
));
const CommentIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaRegCommentAlt className="react-icon" />
  </span>
));
const PaperclipIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaPaperclip className="react-icon" />
  </span>
));

const ClockIcon = injectIntl(({ intl, tooltip, disabled, clickable, className, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaRegClock
      className={classNames(className, {
        'react-icon': clickable,
        'read-only greyed-out': disabled,
      })}
    />
  </span>
));

const MainTaskIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaClipboardList
      className="margin5R text-blue"
      style={{ height: '18px', width: '18px', verticalAlign: 'sub' }}
    />
  </span>
));

const SubTaskIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaIdBadge
      className="margin5R text-orange"
      style={{ height: '18px', width: '18px', verticalAlign: 'sub' }}
    />
  </span>
));

const ToDoIcon = injectIntl(({ intl, tooltip, className, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaThumbtack
      className={classNames('text-red-light', className)}
      style={{ height: '18px', width: '18px', verticalAlign: 'sub' }}
    />
  </span>
));

const CopyIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip || intl.formatMessage(messages.Copy)} {...rest}>
    <FaRegCopy className="react-icon" />
  </span>
));
const DownloadIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip || intl.formatMessage(messages.Download)} {...rest}>
    <FaCloudDownloadAlt className="react-icon" />
  </span>
));
const LinkIcon = injectIntl(({ intl, tooltip, disabled, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaExternalLinkAlt className={classNames('react-icon', { 'read-only greyed-out': disabled })} />
  </span>
));
const UrlIcon = injectIntl(({ intl, tooltip, disabled, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaLink className={classNames('react-icon', { 'read-only greyed-out': disabled })} />
  </span>
));
const WindowIcon = injectIntl(({ intl, tooltip, disabled, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaRegWindowRestore
      className={classNames('react-icon', { 'read-only greyed-out': disabled })}
    />
  </span>
));

const AddIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span className="btn-icon" title={tooltip || intl.formatMessage(messages.Add)} {...rest}>
    <FaPlus className="react-icon" />
  </span>
));
const MagicIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span className="btn-icon" title={tooltip} {...rest}>
    <FaMagic className="react-icon" />
  </span>
));
const TreeIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaTree
      className="margin5R text-success"
      style={{ height: '18px', width: '18px', verticalAlign: 'sub' }}
    />
  </span>
));
const TagIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaTag
      className="margin5R text-info"
      style={{ height: '18px', width: '18px', verticalAlign: 'sub' }}
    />
  </span>
));
const FolderIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaStream
      className="margin5R "
      style={{ height: '18px', width: '18px', verticalAlign: 'sub' }}
    />
  </span>
));
const UserIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} style={{ verticalAlign: 'baseline' }} {...rest}>
    <FaUserAlt className="react-icon" />
  </span>
));
const DocumentIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} style={{ verticalAlign: 'baseline' }} {...rest}>
    <FaFileAlt className="react-icon" />
  </span>
));

const EyeIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} style={{ verticalAlign: 'baseline' }} {...rest}>
    <FaEye className="react-icon" />
  </span>
));

const PersonsIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} style={{ verticalAlign: 'text-top', fontSize: '17px' }} {...rest}>
    <FaUserFriends className="react-icon" />
  </span>
));
const EmptyStarIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} style={{ verticalAlign: 'text-top', fontSize: '17px' }} {...rest}>
    <FaRegStar className="react-icon" />
  </span>
));

const StarIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} style={{ verticalAlign: 'text-top', fontSize: '17px' }} {...rest}>
    <FaStar className="react-icon" />
  </span>
));
const BookmarkIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} style={{ verticalAlign: 'text-top', fontSize: '17px' }} {...rest}>
    <FaBookmark className="pointer" />
  </span>
));
const EmptyBookmarkIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} style={{ verticalAlign: 'text-top', fontSize: '17px' }} {...rest}>
    <FaRegBookmark className="pointer" />
  </span>
));

const PhoneIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaPhone
      style={{
        fontSize: '15px',
        color: '#6fba1c',
        position: 'relative',
        top: '0.125em',
      }}
    />
  </span>
));

const NoteIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <TiMessage
      style={{
        fontSize: '15px',
        color: '#757575',
        position: 'relative',
        top: '0.125em',
      }}
    />
  </span>
));
const AppointmentIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <TiMessages
      style={{
        fontSize: '15px',
        color: '#757575',
        position: 'relative',
        top: '0.125em',
      }}
    />
  </span>
));
const MailIcon = injectIntl(({ intl, tooltip, color, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaEnvelope
      style={{
        color: color ?? '#03a9f4',
        position: 'relative',
        top: '0.125em',
      }}
    />
  </span>
));
const EnvelopeIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaEnvelope className="react-icon" />
  </span>
));

const UserCircleIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaRegUser />
  </span>
));
const ReplaceIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip || intl.formatMessage(messages.Replace)} {...rest}>
    <FaExchangeAlt className="react-icon" />
  </span>
));
const SpinnerIcon = () => <i className="fa fa-spinner fa-spin react-icon" />;

const ShareIcon = injectIntl(({ intl, tooltip, clickable, disabled, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaShareAlt
      className={classNames({
        'react-icon': clickable,
        'read-only greyed-out': disabled,
      })}
    />
  </span>
));

const PdfIcon = injectIntl(({ intl, tooltip, className, clickable, disabled, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaFilePdf
      className={classNames('margin5R', className, {
        'react-icon': clickable,
        'read-only greyed-out': disabled,
      })}
    />
  </span>
));

const UserAddIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} style={{ verticalAlign: 'baseline' }} {...rest}>
    <FaUserPlus className="react-icon" />
  </span>
));

const CircleCheckIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip} {...rest}>
    <FaCheckCircle />
  </span>
));

const CancelIcon = injectIntl(({ intl, tooltip, ...rest }) => (
  <span title={tooltip || intl.formatMessage(messages.Cancel)} {...rest}>
    <FaBan className="react-icon" />
  </span>
));

export const IndustryIcon = ({ ...rest }) => (
  <span {...rest}>
    <FaIndustry className="react-icon" />
  </span>
);

export const BriefcaseIcon = ({ ...rest }) => (
  <span {...rest}>
    <FaBriefcase className="react-icon" />
  </span>
);

export const UserFaIcon = ({ ...rest }) => (
  <span {...rest}>
    <FaUser className="react-icon" />
  </span>
);
export const SharepointIcon = ({ ...rest }) => (
  <span {...rest}>
    <svg
      className="react-icon"
      stroke="currentColor"
      fill="currentColor"
      stroke-width="0"
      role="img"
      viewBox="0 0 24 24"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M24 13.5q0 1.242-.475 2.332-.474 1.09-1.289 1.904-.814.815-1.904 1.29-1.09.474-2.332.474-.762 0-1.523-.2-.106.997-.557 1.858-.451.862-1.154 1.494-.704.633-1.606.99-.902.358-1.91.358-1.09 0-2.045-.416-.955-.416-1.664-1.125-.709-.709-1.125-1.664Q6 19.84 6 18.75q0-.188.018-.375.017-.188.04-.375H.997q-.41 0-.703-.293T0 17.004V6.996q0-.41.293-.703T.996 6h3.54q.14-1.277.726-2.373.586-1.096 1.488-1.904Q7.652.914 8.807.457 9.96 0 11.25 0q1.395 0 2.625.533T16.02 1.98q.914.915 1.447 2.145T18 6.75q0 .188-.012.375-.011.188-.035.375 1.242 0 2.344.469 1.101.468 1.928 1.277.826.809 1.3 1.904Q24 12.246 24 13.5zm-12.75-12q-.973 0-1.857.34-.885.34-1.577.943-.691.604-1.154 1.43Q6.2 5.039 6.06 6h4.945q.41 0 .703.293t.293.703v4.945l.21-.035q.212-.75.61-1.424.399-.673.944-1.218.545-.545 1.213-.944.668-.398 1.43-.61.093-.503.093-.96 0-1.09-.416-2.045-.416-.955-1.125-1.664-.709-.709-1.664-1.125Q12.34 1.5 11.25 1.5zM6.117 15.902q.54 0 1.06-.111.522-.111.932-.37.41-.257.662-.679.252-.422.252-1.055 0-.632-.263-1.054-.264-.422-.662-.703-.399-.282-.856-.463l-.855-.34q-.399-.158-.662-.334-.264-.176-.264-.445 0-.2.14-.323.141-.123.335-.193.193-.07.404-.094.21-.023.351-.023.598 0 1.055.152.457.153.95.457V8.543q-.282-.082-.522-.14-.24-.06-.475-.1-.234-.041-.486-.059-.252-.017-.557-.017-.515 0-1.054.117-.54.117-.979.375-.44.258-.715.68-.275.421-.275 1.03 0 .598.263.997.264.398.663.68.398.28.855.474l.856.363q.398.17.662.358.263.187.263.457 0 .222-.123.351-.123.13-.31.2-.188.07-.393.087-.205.018-.369.018-.703 0-1.248-.234-.545-.235-1.107-.621v1.875q1.195.468 2.472.468zM11.25 22.5q.773 0 1.453-.293t1.19-.803q.51-.51.808-1.195.299-.686.299-1.459 0-.668-.223-1.277-.222-.61-.62-1.096-.4-.486-.95-.826-.55-.34-1.207-.48v1.933q0 .41-.293.703t-.703.293H7.57q-.07.375-.07.75 0 .773.293 1.459t.803 1.195q.51.51 1.195.803.686.293 1.459.293zM18 18q.926 0 1.746-.352.82-.351 1.436-.966.615-.616.966-1.43.352-.815.352-1.752 0-.926-.352-1.746-.351-.82-.966-1.436-.616-.615-1.436-.966Q18.926 9 18 9t-1.74.357q-.815.358-1.43.973t-.973 1.43q-.357.814-.357 1.74 0 .129.006.258t.017.258q.551.27 1.02.65t.838.855q.369.475.627 1.026.258.55.387 1.148Q17.18 18 18 18Z"></path>
    </svg>
  </span>
);

//SiMicrosoftonedrive
export const OneDriveIcon = ({ ...rest }) => (
  <span {...rest}>
    <svg
      className="react-icon"
      stroke="currentColor"
      fill="currentColor"
      stroke-width="0"
      role="img"
      viewBox="0 0 24 24"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M19.453 9.95q.961.058 1.787.468.826.41 1.442 1.066.615.657.966 1.512.352.856.352 1.816 0 1.008-.387 1.893-.386.885-1.049 1.547-.662.662-1.546 1.049-.885.387-1.893.387H6q-1.242 0-2.332-.475-1.09-.475-1.904-1.29-.815-.814-1.29-1.903Q0 14.93 0 13.688q0-.985.31-1.887.311-.903.862-1.658.55-.756 1.324-1.325.774-.568 1.711-.861.434-.129.85-.187.416-.06.861-.082h.012q.515-.786 1.207-1.413.691-.627 1.5-1.066.808-.44 1.705-.668.896-.229 1.845-.229 1.278 0 2.456.417 1.177.416 2.144 1.16.967.744 1.658 1.78.692 1.038 1.008 2.28zm-7.265-4.137q-1.325 0-2.52.544-1.195.545-2.04 1.565.446.117.85.299.405.181.792.416l4.78 2.86 2.731-1.15q.27-.117.545-.204.276-.088.58-.147-.293-.937-.855-1.705-.563-.768-1.319-1.318-.755-.551-1.658-.856-.902-.304-1.886-.304zM2.414 16.395l9.914-4.184-3.832-2.297q-.586-.351-1.23-.539-.645-.188-1.325-.188-.914 0-1.722.364-.809.363-1.412.978-.604.616-.955 1.436-.352.82-.352 1.723 0 .703.234 1.423.235.721.68 1.284zm16.711 1.793q.563 0 1.078-.176.516-.176.961-.516l-7.23-4.324-10.301 4.336q.527.328 1.13.504.604.175 1.237.175zm3.012-1.852q.363-.727.363-1.523 0-.774-.293-1.407t-.791-1.072q-.498-.44-1.166-.68-.668-.24-1.406-.24-.422 0-.838.1t-.815.252q-.398.152-.785.334-.386.181-.761.345Z"></path>
    </svg>
  </span>
);

export {
  UserCircleIcon,
  EnvelopeIcon,
  IconPencil,
  IconWarningSign,
  DeleteIcon,
  UndoIcon,
  ListIcon,
  InfoCircleIcon,
  CommentIcon,
  MainTaskIcon,
  SubTaskIcon,
  ClockIcon,
  CopyIcon,
  AddIcon,
  MagicIcon,
  LinkIcon,
  ToDoIcon,
  SaveIcon,
  DownloadIcon,
  LeftArrowIcon,
  RightArrowIcon,
  TreeIcon,
  TagIcon,
  FolderIcon,
  UserIcon,
  WindowIcon,
  DocumentIcon,
  EyeIcon,
  PersonsIcon,
  EmptyStarIcon,
  StarIcon,
  BookmarkIcon,
  EmptyBookmarkIcon,
  PhoneIcon,
  NoteIcon,
  AppointmentIcon,
  MailIcon,
  ReplaceIcon,
  SpinnerIcon,
  PaperclipIcon,
  FolderOpenIcon,
  ShareIcon,
  PaperPlaneIcon,
  HistoryIcon,
  PdfIcon,
  UrlIcon,
  UserAddIcon,
  CircleCheckIcon,
  CancelIcon,
};
